import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { authApi, SignupData, ApiError } from '../services/api';
import { X, User, Mail, Lock, Eye, EyeOff, AlertCircle, UserPlus, CheckCircle } from 'lucide-react';

interface SignupModalProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
  onSwitchToVerification: (email: string) => void;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

const SignupModal: React.FC<SignupModalProps> = ({ onClose, onSwitchToLogin, onSwitchToVerification }) => {
  const [formData, setFormData] = useState<SignupData>({
    name: '',
    email: '',
    password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (fieldName: string) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    validateField(fieldName);
  };

  const validateField = (fieldName: string): boolean => {
    let isValid = true;
    const newErrors = { ...errors };

    switch (fieldName) {
      case 'name':
        if (!formData.name.trim()) {
          newErrors.name = 'Full name is required';
          isValid = false;
        } else if (formData.name.trim().length < 2) {
          newErrors.name = 'Name must be at least 2 characters';
          isValid = false;
        } else {
          delete newErrors.name;
        }
        break;

      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email) {
          newErrors.email = 'Email is required';
          isValid = false;
        } else if (!emailRegex.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
          isValid = false;
        } else {
          delete newErrors.email;
        }
        break;

      case 'password':
        if (!formData.password) {
          newErrors.password = 'Password is required';
          isValid = false;
        } else if (formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
          isValid = false;
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
          newErrors.password = 'Must contain uppercase, lowercase, and a number';
          isValid = false;
        } else {
          delete newErrors.password;
        }
        break;

      case 'confirmPassword':
        if (!confirmPassword) {
          newErrors.confirmPassword = 'Please confirm your password';
          isValid = false;
        } else if (formData.password !== confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
          isValid = false;
        } else {
          delete newErrors.confirmPassword;
        }
        break;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateForm = (): boolean => {
    const fields = ['name', 'email', 'password', 'confirmPassword'];
    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field)) {
        isValid = false;
      }
    });

    setTouched({
      name: true,
      email: true,
      password: true,
      confirmPassword: true,
    });

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await authApi.signup(formData);
      onSwitchToVerification(formData.email);
    } catch (error) {
      const apiError = error as { response?: { data?: ApiError } };
      if (apiError.response?.data) {
        const errorData = apiError.response.data;
        if (errorData.errors) {
          setErrors({
            email: errorData.errors.email?.[0],
            name: errorData.errors.name?.[0],
            password: errorData.errors.password?.[0],
            general: errorData.message || 'Signup failed',
          });
        } else {
          setErrors({ general: errorData.message || 'Signup failed' });
        }
      } else {
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldError = (fieldName: string) => {
    return touched[fieldName] ? errors[fieldName as keyof FormErrors] : undefined;
  };

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative bg-gradient-to-br from-fintech-primary to-blue-800 px-8 pt-8 pb-10">
          <button
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-4">
            <UserPlus size={22} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">Create Account</h2>
          <p className="text-blue-200 text-sm">Join Kenluk Pay and start procuring globally</p>
        </div>

        {/* Form */}
        <div className="px-8 py-7">
          {errors.general && (
            <div className="alert alert-error mb-5">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span>{errors.general}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Full Name */}
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="signup-name">Full Name</label>
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                <input
                  type="text"
                  id="signup-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                  className={`form-input !pl-10 ${getFieldError('name') ? 'error' : ''}`}
                  placeholder="John Doe"
                  required
                />
              </div>
              {getFieldError('name') && (
                <p className="form-error">
                  <AlertCircle size={12} />
                  {getFieldError('name')}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="signup-email">Email Address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                <input
                  type="email"
                  id="signup-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur('email')}
                  className={`form-input !pl-10 ${getFieldError('email') ? 'error' : ''}`}
                  placeholder="you@company.com"
                  required
                />
              </div>
              {getFieldError('email') && (
                <p className="form-error">
                  <AlertCircle size={12} />
                  {getFieldError('email')}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="signup-password">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="signup-password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                  className={`form-input !pl-10 !pr-10 ${getFieldError('password') ? 'error' : ''}`}
                  placeholder="Min. 6 characters"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted hover:text-fintech-text transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {getFieldError('password') && (
                <p className="form-error">
                  <AlertCircle size={12} />
                  {getFieldError('password')}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="signup-confirm-password">Confirm Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted pointer-events-none" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="signup-confirm-password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleChange}
                  onBlur={() => handleBlur('confirmPassword')}
                  className={`form-input !pl-10 !pr-10 ${getFieldError('confirmPassword') ? 'error' : ''}`}
                  placeholder="Repeat your password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-fintech-textMuted hover:text-fintech-text transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {getFieldError('confirmPassword') && (
                <p className="form-error">
                  <AlertCircle size={12} />
                  {getFieldError('confirmPassword')}
                </p>
              )}
            </div>

            {/* Password requirements hint */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <CheckCircle size={14} className="text-fintech-secondary flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                Password must be at least 6 characters with uppercase, lowercase, and a number.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn-primary w-full justify-center py-3"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner w-4 h-4" />
                  Creating Account...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  Create Account
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="divider mt-6">
            <span>Already have an account?</span>
          </div>
          <button
            type="button"
            className="btn-secondary w-full justify-center"
            onClick={onSwitchToLogin}
          >
            Sign In Instead
          </button>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default SignupModal;
